import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ScoreRing, StarScore } from "@/components/ScoreRing";
import { ToolLogo } from "@/components/ToolLogo";
import { api } from "@/lib/api";
import { ExternalLink, ArrowLeft, Sparkles, Heart, Layers, GitCompare } from "lucide-react";

const card = "rounded-md bg-white border border-[#E7EAF0] p-6";
const label = "text-[10px] uppercase tracking-[0.24em] font-bold text-[#5B6478]";

export default function ToolDetail() {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/tools/${slug}`).then(r => setTool(r.data)).catch(() => {
      api.get(`/agents/${slug}`).then(r => setTool(r.data));
    });
  }, [slug]);

  useEffect(() => {
    if (tool) api.get(`/tools/${slug}/similar`).then(r => setRelated(r.data));
  }, [tool, slug]);

  if (!tool) return <Layout hideRightPanel><div className="skeleton h-96 rounded-md" /></Layout>;

  return (
    <Layout hideRightPanel>
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-[#5B6478] hover:text-[#0B1220] mb-6 transition-colors"
        data-testid="back-link"
      >
        <ArrowLeft size={14} /> Back to directory
      </Link>

      {/* ── Header row ─────────────────────────────────────────── */}
      <header className="flex flex-col md:flex-row md:items-start gap-6 mb-8" data-testid="tool-header">
        <ToolLogo tool={tool} size={80} rounded="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <div className={label}>{tool.category}</div>
            {tool.rank ? (
              <div
                className="inline-flex items-center gap-1 rounded-md bg-[#0B1220] text-white text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5"
                data-testid={`rank-badge-detail-${tool.slug}`}
                title={`Ranked #${tool.rank} of 500 by monthly visits`}
              >
                <span className="opacity-60">#</span>{tool.rank} <span className="opacity-60 font-body font-medium normal-case tracking-normal ml-0.5">most-used</span>
              </div>
            ) : null}
            {tool.hidden_gem && <span className="text-[10px] font-bold uppercase tracking-widest text-[#0B1220] bg-[#F6F7FB] px-2 py-0.5 rounded-md">Hidden Gem</span>}
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold leading-none tracking-tight text-[#0B1220]">{tool.name}</h1>
          <p className="text-lg text-[#3D465C] mt-3 leading-relaxed max-w-3xl">{tool.tagline}</p>
          <div className="flex items-center gap-2 mt-5 flex-wrap">
            <a
              href={tool.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 px-4 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 hover:brightness-110 transition-all"
              data-testid="visit-btn"
            >
              Try Now <ExternalLink size={12} />
            </a>
            <button
              className="h-9 px-4 rounded-md bg-white text-[#0B1220] border border-[#E7EAF0] text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:border-[#0B1220]/40 transition-colors"
              data-testid="add-stack-btn"
            >
              <Layers size={12} /> Add to Stack
            </button>
            <button
              className="h-9 px-4 rounded-md bg-white text-[#0B1220] border border-[#E7EAF0] text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:border-[#0B1220]/40 transition-colors"
              data-testid="pairings-btn"
            >
              <GitCompare size={12} /> Pairings
            </button>
            <button
              className="h-9 w-9 rounded-md bg-white text-[#0B1220] border border-[#E7EAF0] inline-flex items-center justify-center hover:border-[#0B1220]/40 transition-colors"
              data-testid="fav-btn"
              aria-label="Favorite"
            >
              <Heart size={13} />
            </button>
            <span className="text-[11px] px-2.5 py-1 rounded-md bg-[#F6F7FB] border border-[#E7EAF0] font-semibold text-[#0B1220] ml-1">{tool.price_label || tool.pricing}</span>
          </div>
        </div>
      </header>

      {/* ── Scores strip ──────────────────────────────────────── */}
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 ${card}`} data-testid="scores-panel">
        <div className="flex items-center justify-center"><ScoreRing score={tool.curio_score} size={72} /></div>
        <div className="flex items-center justify-center"><StarScore score={tool.user_score} votes={tool.user_votes} /></div>
        <div>
          <div className={`${label} mb-1`}>Pricing</div>
          <div className="font-semibold text-[#0B1220]">{tool.pricing}</div>
          <div className="text-xs text-[#5B6478] mt-1">{tool.price_label}</div>
        </div>
        <div>
          <div className={`${label} mb-1`}>Tags</div>
          <div className="flex flex-wrap gap-1.5">
            {tool.has_free_tier && <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-800 font-semibold">Free Tier</span>}
            {tool.letter_grade && <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#0B1220] text-white font-mono font-bold">{tool.letter_grade}</span>}
          </div>
        </div>
      </div>

      {/* ── What it does ───────────────────────────────────────── */}
      {tool.brief && (
        <section className="mb-8">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-3 text-[#0B1220]">What it actually does</h2>
          <p className="text-[#3D465C] leading-relaxed max-w-3xl">{tool.brief}</p>
        </section>
      )}

      {tool.how_it_works && (
        <section className="mb-8">
          <h2 className="font-display text-xl font-bold tracking-tight mb-3 text-[#0B1220]">How it works — simply</h2>
          <p className="text-[#3D465C] leading-relaxed max-w-3xl">{tool.how_it_works}</p>
        </section>
      )}

      {/* ── Pros / Cons side-by-side ──────────────────────────── */}
      {(tool.pros?.length || tool.cons?.length) ? (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className={card}>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700 mb-3">Strengths</div>
            <ul className="space-y-2">
              {(tool.pros || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm text-[#0B1220]"><span className="text-emerald-600 font-bold">+</span>{p}</li>
              ))}
            </ul>
          </div>
          <div className={card}>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-rose-700 mb-3">Weaknesses</div>
            <ul className="space-y-2">
              {(tool.cons || []).map((p) => (
                <li key={p} className="flex gap-2 text-sm text-[#0B1220]"><span className="text-rose-500 font-bold">–</span>{p}</li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {/* ── Best for / Avoid if ───────────────────────────────── */}
      {(tool.best_for?.length > 0 || tool.avoid_if?.length > 0) && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {tool.best_for?.length > 0 && (
            <div className={card}>
              <div className={`${label} mb-3`}>Best for</div>
              <ul className="space-y-1.5 text-sm text-[#0B1220]">{tool.best_for.map(p => <li key={p}>· {p}</li>)}</ul>
            </div>
          )}
          {tool.avoid_if?.length > 0 && (
            <div className={card}>
              <div className={`${label} mb-3`}>Avoid if</div>
              <ul className="space-y-1.5 text-sm text-[#0B1220]">{tool.avoid_if.map(p => <li key={p}>· {p}</li>)}</ul>
            </div>
          )}
        </section>
      )}

      {/* ── Value breakdown ───────────────────────────────────── */}
      {tool.value_breakdown && (
        <section className={`mb-8 ${card}`} data-testid="value-breakdown">
          <h2 className="font-display text-xl font-bold tracking-tight mb-4 text-[#0B1220]">Value Score breakdown · <span className="font-mono">{tool.value_score}/100</span></h2>
          <div className="space-y-3">
            {Object.entries(tool.value_breakdown).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1 text-[#0B1220]"><span className="capitalize">{k.replace(/_/g, ' ')}</span><span className="font-mono font-bold">{v}</span></div>
                <div className="h-1.5 bg-[#F6F7FB] rounded-full overflow-hidden"><div className="h-full bg-[#0B1220]" style={{ width: `${v}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="text-xs text-[#5B6478] mt-4">Weighted: usefulness 35% + reliability 25% + pricing value 25% + ease of use 15%. <Link to="/methodology" className="underline">How we score →</Link></div>
        </section>
      )}

      {tool.performance && (
        <section className="mb-8">
          <h2 className="font-display text-xl font-bold tracking-tight mb-3 text-[#0B1220]">Real-world performance</h2>
          <p className="text-[#3D465C] leading-relaxed max-w-3xl">{tool.performance}</p>
        </section>
      )}

      {tool.ethics && (
        <section className={`mb-8 ${card} border-l-2 !border-l-[#0B1220]`}>
          <div className={`${label} mb-2`}>Ethical considerations & limitations</div>
          <p className="text-sm leading-relaxed text-[#0B1220]">{tool.ethics}</p>
        </section>
      )}

      {tool.bottom_line && (
        <section className={`mb-8 ${card}`}>
          <div className={`${label} mb-2`}>Bottom line</div>
          <p className="font-display text-2xl leading-snug text-[#0B1220]">{tool.bottom_line}</p>
        </section>
      )}

      {tool.concept && (
        <section className={`mb-8 ${card}`}>
          <div className={`${label} mb-2 inline-flex items-center gap-2`}><Sparkles size={12} /> Learn the concept</div>
          <p className="font-display text-2xl leading-snug text-[#0B1220]">{tool.concept}</p>
        </section>
      )}

      {tool.verified_at && (
        <div className="mb-8 text-xs uppercase tracking-[0.24em] text-[#8A93A6] font-bold">
          Verified on {tool.verified_at} · <Link to="/methodology" className="text-[#0B1220] underline">Methodology</Link>
        </div>
      )}

      {tool.capabilities && (
        <section className={`mb-10 ${card}`} data-testid="capability-matrix">
          <h2 className="font-display text-xl font-bold tracking-tight mb-4 text-[#0B1220]">Agent capability matrix</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(tool.capabilities).map(([k, v]) => (
              <div key={k} className="text-center border border-[#E7EAF0] rounded-md p-3 bg-white">
                <div className="text-2xl">{v ? "✓" : "—"}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#5B6478] mt-1">{k}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section data-testid="see-similar">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-4 text-[#0B1220]">See similar tools</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {related.map(r => (
              <Link
                key={r.slug}
                to={`/tool/${r.slug}`}
                className="rounded-md bg-white border border-[#E7EAF0] p-4 hover:border-[#0B1220]/40 transition-colors"
              >
                <ToolLogo tool={r} size={40} rounded="md" className="mb-2" />
                <div className="font-semibold text-sm text-[#0B1220]">{r.name}</div>
                <div className="text-xs text-[#5B6478] mt-1 line-clamp-2">{r.tagline}</div>
                <div className="flex items-center gap-2 mt-2 text-[10px] font-bold">
                  <span className="font-mono text-[#0B1220]">Wikifi {r.curio_score}</span>
                  {r.letter_grade && <span className="text-[#5B6478]">{r.letter_grade}</span>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
